import { Model } from 'modules/core/lib/mobx-rest'

const ENFORCEMENTS = {
  blockage: 'blockage',
  recommendation: 'recommendation',
}

type Enforcement = keyof typeof ENFORCEMENTS

const MANDATORY_FIELDS = {
  first_name: 'first_name',
  last_name: 'last_name',
  social_security_number: 'social_security_number',
  identifier: 'identifier',
  identifier_type: 'identifier_type',
  address: 'address',
  gender: 'gender',
  birthday_on: 'birthday_on',
  es_contract_type: 'es_contract_type',
  salary_amount: 'salary_amount',
  working_hours: 'working_hours',
  working_hours_frequency: 'working_hours_frequency',
  working_week_days: 'working_week_days',
  es_professional_category_name: 'es_professional_category_name',
  terminated_on: 'terminated_on',
  termination_reason: 'termination_reason',
  remaining_holidays: 'remaining_holidays',
  starts_on: 'starts_on',
  description: 'description',
  bank_number: 'bank_number',
  fr_contract_type_name: 'fr_contract_type_name',
  fr_coefficient: 'fr_coefficient',
  fr_mutual_name: 'fr_mutual_name',
  company_identifier: 'company_identifier',
  seniority_calculation_date: 'seniority_calculation_date',
  fr_work_type_name: 'fr_work_type_name',
  ends_on: 'ends_on',
} as const

export type MandatoryFieldName = (typeof MANDATORY_FIELDS)[keyof typeof MANDATORY_FIELDS]

export function isMandatoryFieldName(value: string): value is MandatoryFieldName {
  return Object.keys(MANDATORY_FIELDS).includes(value)
}

interface IIncidenceRule {
  company_id: string
  incidence_type: string
  field: MandatoryFieldName
  enforcement: Enforcement
  enforced: boolean
}

export const CATEGORIES = {
  personal: 'personal',
  contract: 'contract',
  termination: 'termination',
  sick_leave: 'sick_leave',
  profile: 'profile',
}

class IncidenceRule extends Model<IIncidenceRule> {
  static urlRoot() {
    return '/bookkeepers_management/options/incidence_rules'
  }

  static MandatoryFields = (): Record<string, MandatoryFieldName[]> => ({
    [CATEGORIES.personal]: [
      MANDATORY_FIELDS.first_name,
      MANDATORY_FIELDS.last_name,
      MANDATORY_FIELDS.social_security_number,
      MANDATORY_FIELDS.identifier,
      MANDATORY_FIELDS.identifier_type,
      MANDATORY_FIELDS.address,
      MANDATORY_FIELDS.gender,
      MANDATORY_FIELDS.birthday_on,
      MANDATORY_FIELDS.bank_number,
    ],
    [CATEGORIES.profile]: [MANDATORY_FIELDS.company_identifier],
    [CATEGORIES.profile]: [MANDATORY_FIELDS.seniority_calculation_date],
    [CATEGORIES.contract]: [
      MANDATORY_FIELDS.salary_amount,
      MANDATORY_FIELDS.es_contract_type,
      MANDATORY_FIELDS.working_hours,
      MANDATORY_FIELDS.working_hours_frequency,
      MANDATORY_FIELDS.working_week_days,
      MANDATORY_FIELDS.es_professional_category_name,
      MANDATORY_FIELDS.fr_coefficient,
      MANDATORY_FIELDS.fr_contract_type_name,
      MANDATORY_FIELDS.fr_mutual_name,
      MANDATORY_FIELDS.fr_work_type_name,
    ],
    [CATEGORIES.termination]: [
      MANDATORY_FIELDS.terminated_on,
      MANDATORY_FIELDS.termination_reason,
      MANDATORY_FIELDS.remaining_holidays,
    ],
    [CATEGORIES.sick_leave]: [MANDATORY_FIELDS.starts_on, MANDATORY_FIELDS.description],
  })

  static MandatoryFieldsByCategory = (): Partial<Record<MandatoryFieldName, string>> => {
    return Object.fromEntries(
      Object.entries(this.MandatoryFields()).flatMap(([category, fields]) =>
        fields.map((field) => [field, category])
      )
    )
  }

  get isBlockage() {
    return this.get('enforcement') === ENFORCEMENTS.blockage
  }
}

export default IncidenceRule
